import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  ProductReviewsApi,
  PublicProductReviewDto,
  PublicProductReviewsResponse,
} from '../../../services/product-reviews-api';

export interface ProductReviewImage {
  id: string;
  url: string;
  sortOrder: number;
}

export interface ProductReviewImage {
  id: string;
  url: string;
  sortOrder: number;
  mediaType: 'Image' | 'Video'; // was missing — needed to avoid rendering video as <img>
}

export interface ProductReview {
  id: string;
  productId: string;
  userName: string;
  userAvatar: string | null;
  rating: number;
  content: string;
  createdAt: string;
  images: ProductReviewImage[];
}

export interface ReviewRatingDistribution {
  stars: number;
  count: number;
  percentage: number;
}

export interface ReviewSummary {
  averageRating: number;
  totalReviews: number;
  // Per-star breakdown (rating1Count..rating5Count) is not available on any public/customer
  // endpoint yet — only on seller/admin summary endpoints. Add back if/when backend confirms
  // a public equivalent. Distribution UI in the template should stay hidden until then.
}

export interface ReviewsFilter {
  /** Exact rating match — the API has no "minimum rating" filter, only exact `rating`. */
  rating?: number;
  hasMedia?: boolean;
}

export interface ReviewsPagedResponse {
  items: ProductReview[];
  page: number;
  pageSize: number;
  totalCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProductReviewsManager {
  private readonly api = inject(ProductReviewsApi);

  private currentProductId = signal<string>('');
  private reviews = signal<ProductReview[]>([]);

  // TODO: averageRating / totalReviews are NOT part of this endpoint's response.
  // Per the API doc, product rating summaries live on product read endpoints now.
  // Call setSummary() from the parent once that data is available — see chat.
  private summary = signal<ReviewSummary | null>(null);

  private isLoading = signal(false);
  currentPage = signal(1);
  private pageSize = signal(10);
  private totalCount = signal(0);
  private currentFilter = signal<ReviewsFilter>({});

  readonly reviewsList = computed(() => this.reviews());
  readonly reviewSummary = computed(() => this.summary());
  readonly loading = computed(() => this.isLoading());
  readonly hasMore = computed(
    () => this.currentPage() * this.pageSize() < this.totalCount(),
  );
  readonly totalPages = computed(() =>
    Math.ceil(this.totalCount() / this.pageSize()),
  );

  loadProductReviews(
    productId: string,
    page: number = 1,
    filter?: any,
  ): Observable<ReviewsPagedResponse> {
    this.currentProductId.set(productId);
    this.currentPage.set(page);
    if (filter) {
      this.currentFilter.set(filter);
    }
    this.isLoading.set(true);

    const activeFilter = filter ?? this.currentFilter();

    return this.api
      .getProductReviews(
        productId,
        page,
        this.pageSize(),
        activeFilter.rating,
        activeFilter.hasMedia,
      )
      .pipe(map((res) => this.mapResponse(res)));
  }

  setReviewsData(response: ReviewsPagedResponse) {
    this.reviews.set(response.items);
    this.totalCount.set(response.totalCount);
    this.isLoading.set(false);
  }

  loadMoreReviews(): Observable<ReviewsPagedResponse> | null {
    if (!this.hasMore() || this.isLoading()) return null;

    const nextPage = this.currentPage() + 1;
    this.isLoading.set(true);
    const activeFilter = this.currentFilter();

    return this.api
      .getProductReviews(
        this.currentProductId(),
        nextPage,
        this.pageSize(),
        activeFilter.rating,
        activeFilter.hasMedia,
      )
      .pipe(map((res) => this.mapResponse(res)));
  }

  appendReviews(response: ReviewsPagedResponse) {
    this.reviews.update((current) => [...current, ...response.items]);
    this.currentPage.set(this.currentPage() + 1);
    this.isLoading.set(false);
  }

  applyFilter(filter: any): void {
    this.currentFilter.set(filter);
    this.currentPage.set(1);
  }

  /** Call from the parent product-detail page once product summary data is available. */
  setSummary(averageRating: number, totalReviews: number) {
    this.summary.set({ averageRating, totalReviews });
  }

  resetState() {
    this.currentProductId.set('');
    this.reviews.set([]);
    this.summary.set(null);
    this.isLoading.set(false);
    this.currentPage.set(1);
    this.totalCount.set(0);
    this.currentFilter.set({});
  }

  private mapResponse(res: PublicProductReviewsResponse): ReviewsPagedResponse {
    return {
      items: res.items.map((dto) => this.mapReview(dto)),
      page: res.page,
      pageSize: res.pageSize,
      totalCount: res.totalCount,
    };
  }

  private mapReview(dto: PublicProductReviewDto): ProductReview {
    return {
      id: dto.productReviewId,
      productId: dto.productId,
      userName: dto.customerDisplayName,
      userAvatar: null, // API never returns an avatar — template's initials fallback already handles this
      rating: dto.rating,
      content: dto.text ?? '',
      createdAt: dto.createdAtUtc,
      images: dto.media.map((m, i) => ({
        id: `${dto.productReviewId}-${i}`, // API has no per-media id, sortOrder+reviewId used instead
        url: m.mediaUrl,
        sortOrder: m.sortOrder,
        mediaType: m.mediaType,
      })),
    };
  }
}
